/**
 * OPS-STAB-002 / OPS-PHASE1-009 — operational module registry (canonical source for JS + Java manifest).
 * Matchers align with admin-service provisioned t_core_uri / t_core_perm / t_core_menu / t_core_access_control.
 */
import { UI_SHELL } from './workflowRoutes.js';
import { MODULE_WORKFLOW } from './operationalWorkflowContract.js';

/**
 * @typedef {object} OperationalModuleDef
 * @property {string} id
 * @property {string[]} permissionKeys exact perm ids (t_core_perm)
 * @property {string[]} permissionPrefixes partial match on perm id
 * @property {string[]} uriPatterns route/API patterns (t_core_uri.uri_details)
 * @property {string[]} menuUriPatterns URIs appearing in menu tree
 * @property {string[]} pageNames t_core_access_control.page_name
 * @property {string} [workflowGroup] t_core_workflow_group.GROUP_CODE
 * @property {string[]} [workflowPermissionKeys] canonical FONE workflow action keys
 * @property {{ HMS_SHELL?: string, HEALTHCARE_UI?: string }} routes
 */

/** @type {Record<string, OperationalModuleDef>} */
const BASE_OPERATIONAL_MODULES = {
  OP_BILLING_COUNTER: {
    id: 'OP_BILLING_COUNTER',
    permissionKeys: ['BILLING_OP_COUNTER', 'BILLING_WORKSPACE', 'OP_BILLING_COUNTER'],
    permissionPrefixes: ['BILLING_OP', 'BILLING_WORKSPACE', 'OP_COUNTER'],
    uriPatterns: [
      '/billing/workspace',
      '/billing/counter',
      '/billing/operations-desk',
      '/billing/ip-admissions/.*',
      '/api/v1/billing/op-counter',
      '/api/v1/billing/op-counter/.*',
    ],
    menuUriPatterns: ['/billing/workspace', '/billing/counter', '/billing/operations'],
    pageNames: ['BillingWorkspace', 'OpBillingCounter', 'UnifiedBillingWorkspace'],
    routes: { HMS_SHELL: '/billing/counter', HEALTHCARE_UI: '/billing/workspace' },
  },
  PATIENT_BILLING_DUES: {
    id: 'PATIENT_BILLING_DUES',
    permissionKeys: ['PATIENTBILLINGDUES', 'PATIENT_BILLING_DUES', 'FONE:PATIENTBILLINGDUES'],
    permissionPrefixes: [],
    uriPatterns: [
      '/billing/patient-dues',
      '/billing/patient-dues/.*',
      '/billing/ip-admissions/.*',
      '/api/v1/billing/patient-dues',
      '/api/v1/billing/patient-dues/.*',
    ],
    menuUriPatterns: ['/billing/patient-dues'],
    pageNames: ['PatientBillingDues'],
    routes: { HMS_SHELL: '/billing/patient-dues', HEALTHCARE_UI: '/billing/patient-dues' },
  },
  PATIENT_JOURNEY: {
    id: 'PATIENT_JOURNEY',
    permissionKeys: ['PATIENT_JOURNEY', 'BILLING_PATIENT_JOURNEY', 'OPERATIONAL_PATIENT_JOURNEY'],
    permissionPrefixes: ['PATIENT_JOURNEY'],
    uriPatterns: ['/workspace/patient', '/billing/patient-journey', '/api/v1/billing/patient-journey', '/api/v1/billing/patient-journey/.*'],
    menuUriPatterns: ['/workspace/patient', '/billing/patient-journey'],
    pageNames: ['PatientJourney', 'PatientOperationalWorkspace'],
    routes: { HMS_SHELL: '/workspace/patient', HEALTHCARE_UI: '/billing/patient-journey' },
  },
  IP_DISCHARGE: {
    id: 'IP_DISCHARGE',
    permissionKeys: ['IP_DISCHARGE', 'DISCHARGE_DESK', 'BILLING_IP_DISCHARGE'],
    permissionPrefixes: ['IP_DISCHARGE', 'DISCHARGE_DESK'],
    uriPatterns: [
      '/billing/ip-discharge',
      '/billing/ip-admissions/.*',
      '/api/v1/billing/ip-discharge',
      '/api/v1/billing/ip-discharge/.*',
    ],
    menuUriPatterns: ['/billing/ip-discharge'],
    pageNames: ['IpDischarge', 'DischargeDesk'],
    routes: { HMS_SHELL: '/billing/ip-discharge', HEALTHCARE_UI: '/billing/ip-discharge' },
  },
  /**
   * Inpatient census / workspace (healthcare-ui).
   * Canonical grant: FONE:INPATIENT (admin-portal V102).
   * Transitional keys (IP_DISCHARGE / DISCHARGE_DESK / BILLING_IP_DISCHARGE) remain so
   * hospitals that have not yet received FONE→tenant provisioning-sync still unlock
   * /inpatient. IP discharge desk stays IP_DISCHARGE / FONE:IPDISCHARGE.
   */
  INPATIENT: {
    id: 'INPATIENT',
    permissionKeys: [
      'INPATIENT',
      'IP_INPATIENT',
      'FONE:INPATIENT',
      // Transitional hospital-compat (remove after all tenants synced from FONE V102)
      'IP_DISCHARGE',
      'DISCHARGE_DESK',
      'BILLING_IP_DISCHARGE',
      'FONE:IPDISCHARGE',
    ],
    permissionPrefixes: ['INPATIENT', 'IP_INPATIENT'],
    uriPatterns: [
      '/inpatient',
      '/inpatient/.*',
      '/api/v1/encounter/admissions',
      '/api/v1/encounter/admissions/.*',
      // Live projected bed board used by Emergency/Direct + OP-recommended admit
      '/api/v1/encounter/beds',
      '/api/v1/encounter/beds/.*',
      '/api/v1/billing/ip-counter',
      '/api/v1/billing/ip-counter/.*',
      // OP→IP admission recommendation worklist / fulfill (clinical-service)
      '/api/v1/clinical/recommendations',
      '/api/v1/clinical/recommendations/.*',
    ],
    menuUriPatterns: ['/inpatient', '/inpatient/admit', '/inpatient/admission-requests'],
    pageNames: [
      'Inpatient',
      'IpPatientList',
      'IpPatientWorkspace',
      'IpAdmissionEntry',
      'IpAdmissionRequests',
    ],
    routes: { HMS_SHELL: '/encounter/admissions', HEALTHCARE_UI: '/inpatient' },
  },
  /**
   * IP payer / credit receivables (billing-service /api/v1/billing/receivables).
   * Mirrors IpReceivablesService.requireReceivables: FONE:BILLINGRECEIVABLES (V151) or the IP discharge desk.
   * Credit approval is a separate explicit action key (FONE:BILLINGCREDITAPPROVE), not module access.
   */
  BILLING_RECEIVABLES: {
    id: 'BILLING_RECEIVABLES',
    permissionKeys: ['BILLING_RECEIVABLES', 'IP_DISCHARGE', 'DISCHARGE_DESK', 'BILLING_IP_DISCHARGE'],
    permissionPrefixes: ['BILLING_RECEIVABLES'],
    uriPatterns: ['/billing/receivables', '/api/v1/billing/receivables', '/api/v1/billing/receivables/.*'],
    menuUriPatterns: ['/billing/receivables'],
    pageNames: ['IpReceivables', 'ReceivablesWorklist'],
    routes: { HMS_SHELL: '/billing/receivables', HEALTHCARE_UI: '/billing/receivables' },
  },
  SHIFT_CONTINUITY: {
    id: 'SHIFT_CONTINUITY',
    permissionKeys: ['SHIFT_CONTINUITY', 'COUNTER_SHIFT', 'BILLING_SHIFT_CONTINUITY'],
    permissionPrefixes: ['SHIFT_CONTINUITY', 'COUNTER_SHIFT'],
    uriPatterns: ['/billing/shift-continuity', '/api/v1/billing/shift-continuity', '/api/v1/billing/shift-continuity/.*'],
    menuUriPatterns: ['/billing/shift-continuity'],
    pageNames: ['ShiftContinuity', 'CounterShiftHandover'],
    routes: { HMS_SHELL: '/billing/shift-continuity', HEALTHCARE_UI: '/billing/shift-continuity' },
  },
  OPERATIONAL_RECOVERY: {
    id: 'OPERATIONAL_RECOVERY',
    permissionKeys: ['OPERATIONAL_RECOVERY', 'FONE:OPERATIONALRECOVERY'],
    permissionPrefixes: ['OPERATIONALRECOVERY'],
    uriPatterns: [
      '/billing/operational-recovery',
      '/api/v1/billing/operational-recovery',
      '/api/v1/billing/operational-recovery/.*',
    ],
    menuUriPatterns: ['/billing/operational-recovery'],
    pageNames: ['OperationalRecovery'],
    routes: {
      HMS_SHELL: '/billing/operational-recovery',
      HEALTHCARE_UI: '/billing/operational-recovery',
    },
  },
  OPERATIONAL_READINESS: {
    id: 'OPERATIONAL_READINESS',
    permissionKeys: ['OPERATIONAL_READINESS', 'FONE:OPERATIONALREADINESS'],
    permissionPrefixes: ['OPERATIONALREADINESS'],
    uriPatterns: [
      '/billing/operational-readiness',
      '/api/v1/billing/operational-readiness',
      '/api/v1/billing/operational-readiness/.*',
    ],
    menuUriPatterns: ['/billing/operational-readiness'],
    pageNames: ['ProductionReadiness', 'OperationalReadiness'],
    routes: {
      HMS_SHELL: '/billing/operational-readiness',
      HEALTHCARE_UI: '/billing/operational-readiness',
    },
  },
  OPERATIONAL_ALERTS: {
    id: 'OPERATIONAL_ALERTS',
    permissionKeys: ['OPERATIONAL_ALERTS', 'BILLING_OPERATIONAL_ALERTS', 'OPS_ALERTS'],
    permissionPrefixes: ['OPERATIONAL_ALERTS', 'OPS_ALERTS'],
    uriPatterns: ['/billing/operational-alerts', '/api/v1/billing/operational-alerts', '/api/v1/billing/operational-alerts/.*'],
    menuUriPatterns: ['/billing/operational-alerts'],
    pageNames: ['OperationalAlerts', 'OperationalActionDesk'],
    routes: { HMS_SHELL: '/billing/operational-alerts', HEALTHCARE_UI: '/billing/operational-alerts' },
  },
  REFUND_REVIEW: {
    id: 'REFUND_REVIEW',
    permissionKeys: ['REFUND_REVIEW', 'BILLING_REFUND_REVIEW', 'SUPERVISOR_REFUND_REVIEW'],
    permissionPrefixes: ['REFUND_REVIEW'],
    uriPatterns: [
      '/billing/refund-review-queue',
      '/api/v1/billing/refund-review',
      '/api/v1/billing/op-counter/refund-review',
      '/api/v1/billing/op-counter/.*refund.*',
    ],
    menuUriPatterns: ['/billing/refund-review-queue'],
    pageNames: ['RefundReviewQueue', 'RefundReview'],
    routes: { HMS_SHELL: '/billing/refund-review-queue', HEALTHCARE_UI: '/billing/refund-review-queue' },
  },
  FINANCE_AUDIT: {
    id: 'FINANCE_AUDIT',
    permissionKeys: ['FINANCE_AUDIT', 'COUNTER_FINANCE_AUDIT', 'BILLING_FINANCE_AUDIT'],
    permissionPrefixes: ['FINANCE_AUDIT', 'COUNTER_AUDIT'],
    uriPatterns: [
      '/billing/counter-finance-audit',
      '/billing/finance-close',
      '/api/v1/billing/counter/.*audit.*',
      '/api/v1/billing/counter/sessions/.*/recovered-close',
      '/api/v1/billing/day-close',
      '/api/v1/billing/day-close/.*',
    ],
    menuUriPatterns: ['/billing/counter-finance-audit', '/billing/finance-close'],
    pageNames: ['CounterFinanceAudit', 'FinanceClose'],
    routes: { HMS_SHELL: '/billing/counter-finance-audit', HEALTHCARE_UI: '/billing/counter-finance-audit' },
  },
  BILLING_DISCOUNT_APPROVAL: {
    id: 'BILLING_DISCOUNT_APPROVAL',
    permissionKeys: ['BILLING_DISCOUNT_APPROVAL'],
    permissionPrefixes: ['BILLING_DISCOUNT', 'OPERATIONAL_APPROVAL'],
    uriPatterns: [
      '/billing/operational-approvals',
      '/api/v1/billing/operational-authorization',
      '/api/v1/billing/operational-authorization/.*',
    ],
    menuUriPatterns: ['/billing/operational-approvals'],
    pageNames: ['OperationalApprovalQueue', 'OperationalApprovals'],
    routes: { HMS_SHELL: '/billing/operational-approvals', HEALTHCARE_UI: '/billing/operational-approvals' },
  },
  LAB_COUNTER: {
    id: 'LAB_COUNTER',
    permissionKeys: ['LAB_COUNTER', 'LAB_QUEUE', 'LAB_WORKLIST', 'LAB_ORDER_DETAIL'],
    permissionPrefixes: ['LAB_COUNTER', 'LAB_COLLECTION', 'LAB_RESULT', 'LAB_PRINT', 'LAB_WORKLIST'],
    uriPatterns: [
      '/lab/worklist',
      '/lab/orders/.*',
      '/lab/counter',
      '/lab/collection',
      '/lab/result-entry',
      '/lab/print-queue',
      '/lab/payments',
      '/api/v1/lab/.*',
    ],
    menuUriPatterns: ['/lab/worklist', '/lab/orders/.*', '/lab/counter', '/lab/collection', '/lab/result-entry', '/lab/print-queue', '/lab/payments'],
    pageNames: ['LabCounter', 'LabWorklist', 'LabOrderDetail'],
    routes: { HMS_SHELL: '/lab/worklist', HEALTHCARE_UI: '/lab/worklist' },
  },
  LAB_VERIFICATION: {
    id: 'LAB_VERIFICATION',
    permissionKeys: ['LAB_VERIFICATION'],
    permissionPrefixes: ['LAB_VERIFICATION'],
    uriPatterns: ['/lab/verification', '/lab/orders/.*', '/api/v1/lab/.*verify.*'],
    menuUriPatterns: ['/lab/verification', '/lab/orders/.*'],
    pageNames: ['LabVerification', 'LabOrderDetail'],
    routes: { HMS_SHELL: '/lab/verification', HEALTHCARE_UI: '/lab/verification' },
  },
  RADIOLOGY_COUNTER: {
    id: 'RADIOLOGY_COUNTER',
    permissionKeys: [
      'RADIOLOGY_COUNTER',
      'RADIOLOGY_VERIFICATION',
      'RAD_VERIFICATION',
      'RADIOLOGY_QUEUE',
    ],
    permissionPrefixes: ['RADIOLOGY_', 'RAD_'],
    uriPatterns: [
      '/radiology/counter',
      '/radiology/scheduling',
      '/radiology/technician',
      '/radiology/report-entry',
      '/radiology/reporting',
      '/radiology/worklist',
      '/radiology/structured-search',
      '/radiology/scan-execution',
      '/radiology/verification',
      '/radiology/print-queue',
      '/radiology/admin/.*',
      '/api/v1/radiology/.*',
    ],
    menuUriPatterns: [
      '/radiology/counter',
      '/radiology/scheduling',
      '/radiology/technician',
      '/radiology/report-entry',
      '/radiology/reporting',
      '/radiology/worklist',
      '/radiology/structured-search',
      '/radiology/scan-execution',
      '/radiology/verification',
      '/radiology/print-queue',
      '/radiology/admin/.*',
    ],
    pageNames: ['RadiologyCounter', 'RadiologyVerification', 'RadiologyReportEntry'],
    routes: { HMS_SHELL: '/radiology/counter', HEALTHCARE_UI: '/radiology/counter' },
  },
  PHARMACY_COUNTER: {
    id: 'PHARMACY_COUNTER',
    permissionKeys: ['PHARMACY_COUNTER', 'PHARMACY_DISPENSE', 'PHARMACY_QUEUE'],
    permissionPrefixes: ['PHARMACYCOUNTER', 'PHARMACY_COUNTER', 'PHARMACY_DISPENSE', 'PHARMACY_QUEUE'],
    uriPatterns: [
      '/pharmacy/counter',
      '/pharmacy/issue',
      '/pharmacy/dispense',
      '/pharmacy/returns',
      '/pharmacy/print-queue',
      // Proceed-to-billing handoff. Not /billing/counter or billing operations.
      '/billing/workspace',
      // Counter / dispense APIs only — never /api/v1/pharmacy/procure/** (assertPharmacyProcure).
      '/api/v1/pharmacy/counter/.*',
      '/api/v1/pharmacy/returns/.*',
      '/api/v1/pharmacy/print/.*',
    ],
    menuUriPatterns: ['/pharmacy/counter', '/pharmacy/issue', '/pharmacy/dispense', '/pharmacy/returns', '/pharmacy/print-queue'],
    pageNames: ['PharmacyCounter', 'PharmacyDispense'],
    routes: { HMS_SHELL: '/pharmacy/counter', HEALTHCARE_UI: '/pharmacy/counter' },
  },
  PHARMACY_PROCURE: {
    id: 'PHARMACY_PROCURE',
    permissionKeys: ['PHARMACY_PROCURE', 'PHARMACYPROCURE', 'FONE:PHARMACYPROCURE'],
    permissionPrefixes: ['PHARMACYPROCURE', 'PHARMACY_PROCURE'],
    uriPatterns: [
      '/pharmacy/procure',
      '/pharmacy/procure/suppliers',
      '/pharmacy/procure/manufacturers',
      '/pharmacy/procure/purchase-orders',
      '/pharmacy/procure/purchase-entry',
      '/pharmacy/procure/purchase-invoices',
      '/pharmacy/procure/invoices',
      '/api/v1/pharmacy/procure/suppliers.*',
      '/api/v1/pharmacy/procure/manufacturers.*',
      '/api/v1/pharmacy/procure/purchase-orders.*',
      '/api/v1/pharmacy/procure/purchase-invoices.*',
    ],
    menuUriPatterns: [
      '/pharmacy/procure/suppliers',
      '/pharmacy/procure/manufacturers',
      '/pharmacy/procure/purchase-orders',
      '/pharmacy/procure/purchase-entry',
      '/pharmacy/procure/purchase-invoices',
    ],
    pageNames: ['PharmacySupplier', 'PharmacyManufacturer', 'PharmacyPurchaseOrder', 'PharmacyPurchaseInvoice'],
    // Explicit procure only — PHARMACYACTION must not open procurement for counter pharmacists.
    workflowPermissionKeys: ['FONE:PHARMACYPROCURE'],
    routes: { HMS_SHELL: '/pharmacy/procure/suppliers', HEALTHCARE_UI: '/pharmacy/procure/suppliers' },
  },
  PHARMACY_GRN: {
    id: 'PHARMACY_GRN',
    permissionKeys: ['PHARMACY_GRN', 'PHARMACYGRN', 'FONE:PHARMACYGRN', 'PHARMACY_PROCURE', 'FONE:PHARMACYPROCURE'],
    permissionPrefixes: ['PHARMACYGRN', 'PHARMACY_GRN', 'PHARMACYPROCURE'],
    uriPatterns: [
      '/pharmacy/procure/grns',
      '/pharmacy/procure/purchase-returns',
      '/api/v1/pharmacy/procure/grns.*',
      '/api/v1/pharmacy/procure/purchase-returns.*',
    ],
    menuUriPatterns: ['/pharmacy/procure/grns', '/pharmacy/procure/purchase-returns'],
    pageNames: ['PharmacyGoodsReceipt', 'PharmacyPurchaseReturn'],
    workflowPermissionKeys: ['FONE:PHARMACYGRN', 'FONE:PHARMACYPROCURE', 'FONE:PHARMACYACTION'],
    routes: { HMS_SHELL: '/pharmacy/procure/grns', HEALTHCARE_UI: '/pharmacy/procure/grns' },
  },
  PHARMACY_INVENTORY: {
    id: 'PHARMACY_INVENTORY',
    permissionKeys: [
      'PHARMACY_INVENTORY',
      'PHARMACYINVENTORY',
      'FONE:PHARMACYINVENTORY',
      'PHARMACY_ADJUST',
      'PHARMACYADJUST',
      'FONE:PHARMACYADJUST',
      'PHARMACY_REPORT',
      'PHARMACYREPORT',
      'FONE:PHARMACYREPORT',
      'FONE:PHARMACYACTION',
    ],
    permissionPrefixes: ['PHARMACYINVENTORY', 'PHARMACY_INVENTORY', 'PHARMACYADJUST', 'PHARMACYREPORT'],
    uriPatterns: [
      '/pharmacy/inventory',
      '/pharmacy/inventory/.*',
      '/pharmacy/expiry',
      '/pharmacy/expiry/.*',
      '/pharmacy/reports',
      '/pharmacy/reports/.*',
      '/api/v1/pharmacy/inventory/.*',
      '/api/v1/pharmacy/stock/.*',
      '/api/v1/pharmacy/reports/.*',
    ],
    menuUriPatterns: [
      '/pharmacy/inventory',
      '/pharmacy/inventory/current-stock',
      '/pharmacy/inventory/inquiry',
      '/pharmacy/inventory/batches',
      '/pharmacy/inventory/batch-history',
      '/pharmacy/inventory/adjustments',
      '/pharmacy/inventory/counts',
      '/pharmacy/inventory/low-stock',
      '/pharmacy/inventory/ledger',
      '/pharmacy/inventory/movements',
      '/pharmacy/inventory/settings',
      '/pharmacy/inventory/fefo',
      '/pharmacy/expiry',
      '/pharmacy/expiry/near',
      '/pharmacy/expiry/expired',
      '/pharmacy/expiry/calendar',
      '/pharmacy/expiry/recalls',
      '/pharmacy/expiry/notifications',
      '/pharmacy/reports',
      '/pharmacy/reports/analytics',
    ],
    pageNames: [
      'PharmacyInventoryDashboard',
      'PharmacyCurrentStock',
      'PharmacyInventoryInquiry',
      'PharmacyBatchInventory',
      'PharmacyStockAdjustment',
      'PharmacyStockCount',
      'PharmacyLowStock',
      'PharmacyStockLedger',
      'PharmacyMovementHistory',
      'PharmacyReports',
      'PharmacyAnalyticsDashboard',
      'PharmacyExpiryDashboard',
      'PharmacyNearExpiry',
      'PharmacyExpiredStock',
      'PharmacyExpiryCalendar',
      'PharmacyRecallCenter',
      'PharmacyNotificationCenter',
    ],
    workflowPermissionKeys: [
      'FONE:PHARMACYINVENTORY',
      'FONE:PHARMACYADJUST',
      'FONE:PHARMACYREPORT',
      'FONE:PHARMACYACTION',
    ],
    routes: { HMS_SHELL: '/pharmacy/inventory', HEALTHCARE_UI: '/pharmacy/inventory' },
  },
  PHARMACY_ADJUST: {
    id: 'PHARMACY_ADJUST',
    permissionKeys: [
      'PHARMACY_ADJUST',
      'PHARMACYADJUST',
      'FONE:PHARMACYADJUST',
      'PHARMACY_INVENTORY',
      'FONE:PHARMACYINVENTORY',
    ],
    permissionPrefixes: ['PHARMACYADJUST', 'PHARMACY_ADJUST'],
    uriPatterns: [
      '/pharmacy/inventory/adjustments',
      '/pharmacy/inventory/counts',
      '/api/v1/pharmacy/inventory/adjustments.*',
      '/api/v1/pharmacy/inventory/counts.*',
    ],
    menuUriPatterns: ['/pharmacy/inventory/adjustments', '/pharmacy/inventory/counts'],
    pageNames: ['PharmacyStockAdjustment', 'PharmacyStockCount'],
    workflowPermissionKeys: ['FONE:PHARMACYADJUST', 'FONE:PHARMACYACTION'],
    routes: {
      HMS_SHELL: '/pharmacy/inventory/adjustments',
      HEALTHCARE_UI: '/pharmacy/inventory/adjustments',
    },
  },
  PHARMACY_REPORT: {
    id: 'PHARMACY_REPORT',
    permissionKeys: [
      'PHARMACY_REPORT',
      'PHARMACYREPORT',
      'FONE:PHARMACYREPORT',
      'PHARMACY_INVENTORY',
      'FONE:PHARMACYINVENTORY',
      'FONE:PHARMACYACTION',
    ],
    permissionPrefixes: ['PHARMACYREPORT', 'PHARMACY_REPORT'],
    uriPatterns: [
      '/pharmacy/reports',
      '/pharmacy/reports/.*',
      '/api/v1/pharmacy/reports',
      '/api/v1/pharmacy/reports/.*',
    ],
    menuUriPatterns: ['/pharmacy/reports', '/pharmacy/reports/analytics'],
    pageNames: ['PharmacyReports', 'PharmacyAnalyticsDashboard'],
    workflowPermissionKeys: ['FONE:PHARMACYREPORT', 'FONE:PHARMACYACTION'],
    routes: { HMS_SHELL: '/pharmacy/reports', HEALTHCARE_UI: '/pharmacy/reports' },
  },
  DOCTOR_CONSULTATION: {
    id: 'DOCTOR_CONSULTATION',
    permissionKeys: [
      'DOCTOR_CONSULTATION',
      'OPD_CONSULTATION',
      'CLINICAL_CONSULTATION',
      'CLINICAL_RECOMMENDATION_ENTRY',
    ],
    permissionPrefixes: ['DOCTOR_CONSULTATION', 'OPD_CONSULT', 'CLINICAL_CONSULT'],
    uriPatterns: [
      '/patients/visits',
      '/patients/visits/opd',
      '/clinical/consultation',
      '/clinical/opd-consultation',
      '/api/v1/clinical/recommendations',
      '/api/v1/clinical/recommendations/.*',
      '/api/v1/clinical/investigation-catalog',
      '/api/v1/clinical/investigation-catalog/.*',
      '/api/v1/clinical/pharmacy-medicines',
      '/api/v1/clinical/pharmacy-medicines/.*',
      '/api/v1/clinical/prescriptions',
      '/api/v1/clinical/prescriptions/.*',
    ],
    menuUriPatterns: [
      '/patients/visits',
      '/patients/visits/opd',
      '/healthcare-portal/opd',
      '/clinical/opd-consultation',
    ],
    pageNames: ['OPDConsultation', 'OpdConsultation', 'ConsultationWorkbench', 'ClinicalRecommendationEntry'],
    routes: {
      HMS_SHELL: '/clinical/opd-consultation',
      HEALTHCARE_UI: '/patients/visits/opd',
    },
  },
  PATIENT_TOKEN: {
    id: 'PATIENT_TOKEN',
    permissionKeys: ['PATIENT_TOKEN', 'RECEPTION_TOKEN', 'FRONT_OFFICE_TOKEN'],
    permissionPrefixes: ['PATIENT_TOKEN', 'RECEPTION_'],
    uriPatterns: [
      '/patients/patient-token',
      '/patients/generate-token',
      '/healthcare-portal/patient-token',
      '/healthcare/patient-token',
      '/patient-token',
      '/reception/.*',
    ],
    menuUriPatterns: [
      '/patients/patient-token',
      '/healthcare-portal/patient-token',
      '/healthcare/patient-token',
    ],
    pageNames: ['PatientToken', 'PatientTokenQueue'],
    routes: { HMS_SHELL: '/workspaces/reception', HEALTHCARE_UI: '/patients/patient-token' },
  },
  PATIENT_REGISTRATION: {
    id: 'PATIENT_REGISTRATION',
    permissionKeys: ['PATIENTS', 'PATIENT_REGISTRATION'],
    permissionPrefixes: [],
    uriPatterns: [
      '/workspaces/reception',
      '/patients',
      '/healthcare/patients',
      '/api/v1/patient',
      '/api/v1/patient/.*',
    ],
    menuUriPatterns: ['/workspaces/reception', '/patients'],
    pageNames: ['ReceptionWorkspace', 'Patients', 'PatientRegistration'],
    routes: { HMS_SHELL: '/workspaces/reception', HEALTHCARE_UI: '/patients' },
  },
};

/** Verbs — must never grant module/nav visibility when present alone on the principal. */
const ACTION_ONLY_PERMISSION_KEYS = new Set([
  'FONE:BILLINGPAYMENT',
  'FONE:REFUNDREQUEST',
  'FONE:PRINTRECEIPT',
  'FONE:SHIFTOPEN',
  'FONE:SHIFTCLOSE',
  'FONE:SHIFTRECOVEREDCLOSE',
  'FONE:LABACTION',
  'FONE:RADACTION',
  'FONE:PHARMACYACTION',
  'FONE:CLINICALACTION',
  'FONE:FINANCECLOSE',
]);

function enrichOperationalModules(base) {
  /** @type {Record<string, OperationalModuleDef>} */
  const out = {};
  for (const [id, def] of Object.entries(base)) {
    const wf = MODULE_WORKFLOW[id];
    const fromContract = (wf?.workflowPermissionKeys ?? []).filter(Boolean);
    const fromDef = (def.workflowPermissionKeys ?? []).filter(Boolean);
    const workflowPermissionKeys = [...new Set([...fromContract, ...fromDef])];
    const baseKeys = (def.permissionKeys ?? []).filter(Boolean);
    /** Keep module-identity workflow keys for desk matching; exclude pure actions. */
    const moduleKeysFromWorkflow = workflowPermissionKeys.filter(
      (k) => !ACTION_ONLY_PERMISSION_KEYS.has(k),
    );
    out[id] = {
      ...def,
      menuUriPatterns: (def.menuUriPatterns ?? []).filter(Boolean),
      uriPatterns: (def.uriPatterns ?? []).filter(Boolean),
      workflowGroup: wf?.workflowGroup ?? def.workflowGroup,
      workflowPermissionKeys,
      actionPermissionKeys: workflowPermissionKeys.filter((k) =>
        ACTION_ONLY_PERMISSION_KEYS.has(k),
      ),
      permissionKeys: [...new Set([...baseKeys, ...moduleKeysFromWorkflow])],
    };
  }
  return out;
}

/** Canonical operational module registry (enriched with workflow contract). */
export const OPERATIONAL_MODULES = enrichOperationalModules(BASE_OPERATIONAL_MODULES);

/** Permissions that unlock all operational modules (provisioned in admin). */
export const OPERATIONAL_GRANT_ALL_PERMISSIONS = [
  'HMS_OPERATIONS_ALL',
  'OPERATIONAL_ALL',
  'SYSTEM_ADMIN_OPERATIONS',
  'FONE:HMSOPERATIONSALL',
];

export const OPERATIONAL_MODULE_IDS = Object.keys(OPERATIONAL_MODULES);

export function moduleRoute(moduleId, shell = UI_SHELL.HEALTHCARE_UI) {
  return OPERATIONAL_MODULES[moduleId]?.routes?.[shell] ?? null;
}
